import { z } from 'zod';
import { RelationTypeSchema, ReferralTypeSchema } from '../enums';

// Referral Schema
export const ReferralSchema = z.object({
  type: ReferralTypeSchema,
  referredBy: z.string().optional(), // Reference to another DonorID
  relationToReferrer: z.string().optional() // e.g., "Son", "Wife", "Father"
});

export type Referral = z.infer<typeof ReferralSchema>;

// Base Donor Schema
export const BaseDonorSchema = z.object({
  donorId: z.string().optional(), // MongoDB ObjectId
  donorName: z.string().min(1, 'Donor name is required').max(100, 'Name too long'),
  fatherOrHusbandName: z.string().min(1, 'Father/Husband name is required').max(100, 'Name too long'),
  relation: RelationTypeSchema,
  cnic: z.string().length(13, 'CNIC must be exactly 13 digits').regex(/^\d{13}$/, 'CNIC must contain only digits'),
  phoneNo: z.string().min(10, 'Phone number must be at least 10 digits').max(15, 'Phone number too long'),
  referral: ReferralSchema.optional(),
  email: z.string().email('Invalid email format').optional(),
  officeNo: z.number().int().positive('Office number must be positive'),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type BaseDonor = z.infer<typeof BaseDonorSchema>;

// Donor Creation Schema
export const CreateDonorSchema = BaseDonorSchema.omit({
  donorId: true,
  createdAt: true,
  updatedAt: true
});

export type CreateDonor = z.infer<typeof CreateDonorSchema>;

// Donor Update Schema
export const UpdateDonorSchema = BaseDonorSchema.partial().extend({
  donorId: z.string() // Required for updates
});

export type UpdateDonor = z.infer<typeof UpdateDonorSchema>;

// Donor Response Schema (for API responses)
export const DonorResponseSchema = BaseDonorSchema.extend({
  donorId: z.string()
});

export type DonorResponse = z.infer<typeof DonorResponseSchema>;

// Donor with Relations Schema
export const DonorWithRelationsSchema = DonorResponseSchema.extend({
  office: z.object({
    officeNo: z.number(),
    officeName: z.string(),
    regionId: z.number(),
    regionName: z.string()
  }).optional(),
  referralDonor: z.object({
    donorId: z.string(),
    donorName: z.string()
  }).optional(),
  totalDonations: z.number().int().min(0).optional(),
  totalAmount: z.number().min(0).optional(),
  lastDonationDate: z.date().optional()
});

export type DonorWithRelations = z.infer<typeof DonorWithRelationsSchema>;

// Donor List Query Schema
export const DonorListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  officeNo: z.number().int().positive().optional(),
  regionId: z.number().int().positive().optional(),
  relation: RelationTypeSchema.optional(),
  isActive: z.boolean().optional(),
  hasDonations: z.boolean().optional(),
  sortBy: z.enum(['donorName', 'cnic', 'phoneNo', 'createdAt', 'lastDonationDate']).default('donorName'),
  sortOrder: z.enum(['asc', 'desc']).default('asc')
});

export type DonorListQuery = z.infer<typeof DonorListQuerySchema>;

// Donor Conversion Schema (from Guest Donor)
export const DonorConversionSchema = z.object({
  guestDonorId: z.string(),
  cnic: z.string().length(13, 'CNIC must be exactly 13 digits'),
  relation: RelationTypeSchema,
  fatherOrHusbandName: z.string().min(1, 'Father/Husband name is required')
});

export type DonorConversion = z.infer<typeof DonorConversionSchema>;

// Donor Statistics Schema
export const DonorStatisticsSchema = z.object({
  donorId: z.string(),
  donorName: z.string(),
  officeNo: z.number(),
  officeName: z.string(),
  totalDonations: z.number(),
  totalAmount: z.number(),
  donationTypes: z.array(z.object({
    type: z.string(),
    count: z.number(),
    amount: z.number()
  })),
  period: z.object({
    start: z.date(),
    end: z.date()
  })
});

export type DonorStatistics = z.infer<typeof DonorStatisticsSchema>;

// Donor Search Schema
export const DonorSearchSchema = z.object({
  query: z.string().min(1, 'Search query is required'),
  officeNo: z.number().int().positive().optional(),
  limit: z.number().int().min(1).max(50).default(10)
});

export type DonorSearch = z.infer<typeof DonorSearchSchema>; 